import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, ScrollText, Target, Skull, Users } from "lucide-react";

const POLL_INTERVAL_MS = 5000;

const PlayerScreen = () => {
  const { playerToken } = useParams();
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [guess, setGuess] = useState({ killer: "", motive: "", weapon: "" });
  const [guessSubmitting, setGuessSubmitting] = useState(false);
  const [showRole, setShowRole] = useState(true);
  const versionRef = useRef(0);
  const pollRef = useRef(null);

  // Initial fetch
  useEffect(() => {
    let cancelled = false;
    api.get(`/play/${playerToken}`).then((r) => {
      if (cancelled) return;
      setState(r.data);
      versionRef.current = r.data.version || 0;
      if (r.data?.my_guess) {
        setGuess({
          killer: r.data.my_guess.killer || "",
          motive: r.data.my_guess.motive || "",
          weapon: r.data.my_guess.weapon || "",
        });
      }
    }).catch((e) => {
      setError(e?.response?.data?.detail || "Link not found");
    });
    return () => { cancelled = true; };
  }, [playerToken]);

  // Light-weight 5s polling for live reveals
  useEffect(() => {
    if (!state) return;
    const tick = async () => {
      try {
        const { data } = await api.get(`/play/${playerToken}/sync`, {
          params: { v: versionRef.current },
        });
        if (data?.changed) {
          setState(data);
          versionRef.current = data.version || versionRef.current;
          toast("The host revealed something new.", { className: "font-hand" });
        }
      } catch (e) {
        // Silent — poll will retry next tick
      }
    };
    pollRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [state, playerToken]);

  const submitGuess = async () => {
    setGuessSubmitting(true);
    try {
      const { data } = await api.post(`/play/${playerToken}/guess`, guess);
      toast.success(`Guess locked in. (${data.score.correct}/3 if the host already knows)`);
      // Refresh my_guess
      const { data: fresh } = await api.get(`/play/${playerToken}`);
      setState(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally {
      setGuessSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="grain min-h-screen flex items-center justify-center px-6" data-testid="player-error">
        <div className="card-noir p-8 max-w-sm text-center">
          <Skull size={32} className="text-[#8b0000] mx-auto mb-3" />
          <div className="font-display text-2xl mb-2">That link is dead.</div>
          <div className="text-sm text-[#a39797]">Ask the host to resend your private link.</div>
        </div>
      </div>
    );
  }

  if (!state) {
    return <div className="grain min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#8b0000]" /></div>;
  }

  const r = state.role_card || {};
  const revealedA = (state.clues_a || []).filter((c) => c.revealed);
  const revealedB = (state.clues_b || []).filter((c) => c.revealed);

  return (
    <div className="grain min-h-screen" data-testid="player-page">
      <div className="max-w-md mx-auto px-5 py-6">
        {/* Header */}
        <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl mb-1">
          case #{state.case_number || "001"} · live
        </div>
        <div className="text-xs tracking-widest text-[#a39797]">
          {state.host_name || "The House"}'s table · {state.event_date}
        </div>

        {/* My Role Card */}
        <div className="card-noir p-5 mt-5" data-testid="role-card">
          <button
            onClick={() => setShowRole((v) => !v)}
            className="flex items-center justify-between w-full"
            data-testid="toggle-role"
          >
            <div className="text-xs tracking-widest text-[#8b0000]">YOUR ROLE · EYES ONLY</div>
            {showRole ? <EyeOff size={16} className="text-[#a39797]" /> : <Eye size={16} className="text-[#a39797]" />}
          </button>
          {showRole && (
            <div className="mt-3">
              <div className="font-display text-3xl mt-1">{r.archetype}</div>
              <div className="font-hand text-[#d4af37] rotate-[-1deg] text-lg mt-1">
                played by {r.player_name}
              </div>
              <p className="text-sm text-[#a39797] mt-3 leading-relaxed">{r.blurb}</p>
              {r.voice && <p className="text-sm mt-3"><span className="text-[#8b0000]">Voice:</span> <span className="text-[#f4f0ec]">{r.voice}</span></p>}
              {r.energy_tag && <p className="text-sm mt-1"><span className="text-[#8b0000]">Your energy:</span> <span className="text-[#f4f0ec]">{r.energy_tag}</span></p>}
              {r.signature_line && <p className="text-sm mt-1"><span className="text-[#8b0000]">Signature line:</span> <span className="text-[#f4f0ec]">"{r.signature_line}"</span></p>}
              {r.accused_secret && <p className="text-sm mt-1"><span className="text-[#8b0000]">You've been "accused" of:</span> <span className="text-[#f4f0ec]">{r.accused_secret}</span></p>}
              {r.is_killer && (
                <div className="mt-4 p-3 border border-[#8b0000] bg-[#8b0000]/10" data-testid="killer-warning">
                  <div className="text-[#8b0000] font-bold text-xs tracking-widest">YOU ARE THE KILLER</div>
                  <div className="text-xs text-[#a39797] mt-1">Lie beautifully. Deflect calmly. The reveal is theirs to earn.</div>
                </div>
              )}
              {r.is_betrayer && r.betrayer_twist && (
                <div className="mt-4 p-3 border border-[#d4af37] bg-[#d4af37]/5">
                  <div className="text-[#d4af37] font-bold text-xs tracking-widest">BETRAYER SIDE-QUEST</div>
                  <div className="text-xs text-[#a39797] mt-1">{r.betrayer_twist}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="card-noir p-5 mt-4" data-testid="roster">
          <div className="flex items-center gap-2 text-xs tracking-widest text-[#a39797] mb-3"><Users size={14} /> AT THE TABLE</div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {(state.players || []).map((p, i) => (
              <div key={i} className={`${p.is_me ? "text-[#d4af37]" : "text-[#f4f0ec]"}`}>
                {p.name}
                {p.is_me && <span className="text-xs text-[#a39797]"> (you)</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Live Clues */}
        {(revealedA.length > 0 || revealedB.length > 0) && (
          <div className="card-noir p-5 mt-4" data-testid="revealed-clues">
            <div className="flex items-center gap-2 text-xs tracking-widest text-[#d4af37] mb-3"><ScrollText size={14} /> CLUES REVEALED</div>
            {revealedA.length > 0 && (
              <>
                <div className="text-xs text-[#8b0000] mt-1 mb-1">Set A</div>
                <ul className="space-y-2 text-sm text-[#f4f0ec]">
                  {revealedA.map((c) => <li key={`a${c.index}`} data-testid={`clue-a-${c.index}`}>· {c.text}</li>)}
                </ul>
              </>
            )}
            {revealedB.length > 0 && (
              <>
                <div className="text-xs text-[#8b0000] mt-3 mb-1">Set B</div>
                <ul className="space-y-2 text-sm text-[#f4f0ec]">
                  {revealedB.map((c) => <li key={`b${c.index}`} data-testid={`clue-b-${c.index}`}>· {c.text}</li>)}
                </ul>
              </>
            )}
          </div>
        )}

        {/* Victim banner */}
        {state.victim_name && (
          <div className="card-noir p-5 mt-4 border-[#8b0000] bg-[#8b0000]/10" data-testid="victim-banner">
            <div className="text-xs text-[#8b0000] tracking-widest">THE VICTIM</div>
            <div className="font-display text-2xl mt-1">{state.victim_name}</div>
            <div className="text-xs text-[#a39797] mt-1">Someone at this table did this.</div>
          </div>
        )}

        {/* Final Reveal */}
        {state.reveal && (
          <div className="card-noir p-5 mt-4 border-[#d4af37]" data-testid="final-reveal">
            <div className="text-xs text-[#d4af37] tracking-widest">THE REVEAL</div>
            <p className="text-sm text-[#f4f0ec] mt-2 whitespace-pre-line leading-relaxed">{state.reveal}</p>
          </div>
        )}

        {/* Guess form (hidden for killer; they already know) */}
        {!r.is_killer && !state.reveal && (
          <div className="card-noir p-5 mt-4" data-testid="guess-form">
            <div className="flex items-center gap-2 text-xs tracking-widest text-[#a39797] mb-3"><Target size={14} /> YOUR GUESS</div>
            <input
              className="input-noir mb-2"
              placeholder="Who did it?"
              value={guess.killer}
              onChange={(e) => setGuess((g) => ({ ...g, killer: e.target.value }))}
              data-testid="guess-killer"
            />
            <input
              className="input-noir mb-2"
              placeholder="Why? (motive)"
              value={guess.motive}
              onChange={(e) => setGuess((g) => ({ ...g, motive: e.target.value }))}
              data-testid="guess-motive"
            />
            <input
              className="input-noir mb-3"
              placeholder="How? (weapon / method)"
              value={guess.weapon}
              onChange={(e) => setGuess((g) => ({ ...g, weapon: e.target.value }))}
              data-testid="guess-weapon"
            />
            <button
              onClick={submitGuess}
              disabled={guessSubmitting || !guess.killer.trim()}
              className="btn-primary w-full disabled:opacity-50"
              data-testid="submit-guess"
            >
              {guessSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Target size={16} />}
              {state.my_guess ? "Update guess" : "Lock in guess"}
            </button>
            {state.my_guess && (
              <div className="text-xs text-[#a39797] mt-2 text-center">
                Locked in at {new Date(state.my_guess.submitted_at).toLocaleTimeString()}
              </div>
            )}
          </div>
        )}

        <div className="text-center text-xs text-[#a39797] mt-6 pb-6">
          wasntme.party · auto-refreshing every 5s
        </div>
      </div>
    </div>
  );
};

export default PlayerScreen;
